// @flow
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { RouterProps } from 'react-router-dom';
import { showAlert } from 'actions/app';
import { CreateMultipleTasksAction, CreationTaskDrawer } from 'features/daily-plan';
import { ORIGIN_TABLE_MAP } from 'features/daily-plan/constants';
import { useTaskDrawerManager } from 'features/daily-plan/hooks';
import {
  CreationSavedSearchDrawer,
  SavedSearchDrawer,
  SearchApplied,
  useSavedSearches
} from 'features/saved-searches';
/** API / EntityRoutes / Endpoints / EntityType */
import useDatatable, {
  addRingCentralContactToStore,
  buildMultiplePhonesActionsRenderer,
  buildRowActionsRendererDefinition,
  getColumnPreferences,
  getColumnsToRender
} from 'hooks/datatable';
import useMultipleSelection, { CustomTableToolbarSelect } from 'hooks/multipleSelection';
import { useSearchProject } from 'hooks/searchProject';
import { useUrlParamsUpdater } from 'hooks/urlParamsUpdater';
import usePageTitle from 'hooks/usePageTitle';
import { useQuickViewPreview } from 'hooks/useQuickViewPreview';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import { useFeatureFlags } from 'providers/FeatureFlagsProvider';
import { useUserContext } from 'providers/UserProvider';
import { userHasRoles } from 'services/Authorization';
import { When } from 'UI/components/atoms/When';
import AutocompleteSelect from 'UI/components/molecules/AutocompleteSelect';
import FPButtonBuilder from 'UI/components/molecules/FPButtonBuilder';
/** Components */
import DataTable from 'UI/components/organisms/DataTable';
import FPQuickView from 'UI/components/organisms/inventoryProfiles/FPQuickView';
import { TableWithSearchProjects } from 'UI/components/organisms/TableWithSearchProjects';
import FiltersLayout from 'UI/components/templates/FiltersLayout';
import { additionalPeriodRanges } from 'UI/components/templates/SideFiltersLayout/filters';
import { backNavigateListConfig } from 'UI/constants/config';
import { CommunicationActionPreset, PageTitles } from 'UI/constants/defaults';
import { Endpoints } from 'UI/constants/endpoints';
import { CandidateEntity, EntityType } from 'UI/constants/entityTypes';
import { FeatureFlags } from 'UI/constants/featureFlags';
import { Roles } from 'UI/constants/roles';
import { createCustomStaticRanges, getFinalColumns } from 'UI/utils';
import { getMinRequiredFilters } from 'UI/utils/filters';

import {
  ColumnsDefinitions,
  DateFilters,
  FiltersGroups,
  PeriodColumnBySection,
  VirtualProps
} from './columns';

const pageKey = 'candidates-list';

const orderByOptions = {
  column: 'id',
  direction: 'asc'
};

const numberOfActionColumns = 1;

const MIN_REQUIRED_FILTERS = getMinRequiredFilters();

const Candidates = (props: RouterProps) => {
  const { history, location } = props;
  const dispatch = useDispatch();
  const [currentUser] = useUserContext();
  const isHighLevelUser = userHasRoles(
    [
      Roles.Operations,
      Roles.DataCoordinator,
      Roles.Leadership,
      Roles.AssistantRegionalDirector,
      Roles.Coach
    ],
    currentUser
  );

  const multiplePhonesActionButtons = buildMultiplePhonesActionsRenderer({
    customName: 'phoneNumbers',
    handleAction,
    columns: getFinalColumns(ColumnsDefinitions),
    entityType: EntityType.Candidate
  });

  const CANDIDATE_COLUMNS = [
    ...getFinalColumns(ColumnsDefinitions),
    multiplePhonesActionButtons
  ].filter(column => !!column);

  const initialPreferences = getColumnPreferences(
    pageKey,
    0,
    orderByOptions,
    CANDIDATE_COLUMNS,
    numberOfActionColumns,
    location.search
  );

  const { checkIfFeatureFlagEnabled } = useFeatureFlags();

  const apiVersion = checkIfFeatureFlagEnabled(FeatureFlags.CandidatesMultiplePhones) ? 2 : 1;
  const canCreateMultipleTasks = checkIfFeatureFlagEnabled(FeatureFlags.DailyPlanAddTasksFromTable);
  const isSavedSearchesEnabled = checkIfFeatureFlagEnabled(FeatureFlags.CandidatesSavedSearches);
  const isSavedSearchesDateEnabled = checkIfFeatureFlagEnabled(FeatureFlags.SavedSearchesDate);
  const [lastAppliedSavedSearchRestoreKey, setLastAppliedSavedSearchRestoreKey] = useState(null);

  const {
    activeSearch,
    appliedPeriod,
    buildSavedSearchPayload,
    getRestoredDateTypeFilter,
    getSavedSearchButtonDefinitions,
    handleApplySearch,
    handleCloseSavedSearchDrawer,
    handleCloseSavedSearchForm,
    handleDeleteSearch,
    handleEditSavedSearchDrawerOpen,
    handleEditSearch,
    handleRemoveSearch,
    isDirty,
    savedDateTypeId,
    savedSearchesUiState,
    savedSearchRestoreKey
  } = useSavedSearches({
    defaultDateTypeFilterId: isSavedSearchesDateEnabled ? DateFilters[0].id : null,
    isDateEnabled: isSavedSearchesDateEnabled,
    preferencesKey: pageKey
  });

  const [dateTypeFilter, setDateTypeFilter] = useState(
    appliedPeriod?.dateTypeFilter || DateFilters[0]
  );

  const {
    activeSection,
    columnPreferences,
    count,
    data,
    filters,
    getData,
    handleColumnDisplayChange,
    handleColumnOrderChange,
    handleColumnSortChange,
    handleFiltersApply,
    handleFiltersChange,
    handleFiltersToggle,
    handleKeywordChange,
    handleOneFilterChange,
    handlePageChange,
    handlePeriodChange: handleDataTablePeriodChange,
    handlePerPageChange,
    handleSectionChange,
    isBelowMinFiltersThreshold,
    listState,
    mainOptions,
    selectedPeriod,
    queryParams
  } = useDatatable({
    apiVersion,
    columnsDefinitions: CANDIDATE_COLUMNS,
    enableSavedSearches: isSavedSearchesEnabled && savedSearchesUiState.isSearching,
    endpoint: Endpoints.Candidates,
    entityName: 'Candidates',
    entityType: EntityType.Candidate,
    initialPreferences,
    key: pageKey,
    minRequiredFilters: !isHighLevelUser ? MIN_REQUIRED_FILTERS : null,
    numberOfActionColumns,
    orderByOptions,
    periodDefaultValue: appliedPeriod || null,
    periodFilterColumn: PeriodColumnBySection,
    initialSection: isSavedSearchesDateEnabled ? savedDateTypeId : DateFilters[0].id,
    savedSearch: isSavedSearchesEnabled && activeSearch && activeSearch?.search,
    sendDateWithTimezone: true,
    shouldScrollOnNavigate: true,
    virtualProps: VirtualProps
  });

  const currentSavedSearchPayload = buildSavedSearchPayload({
    dateTypeFilter,
    filters,
    selectedPeriod
  });

  const restoredDateTypeFilter = getRestoredDateTypeFilter({
    dateFilters: DateFilters,
    fallbackDateFilter: DateFilters[0]
  });

  const handlePeriodChange = useCallback(
    (newPeriod, isFromStaticPeriod) => {
      if (isSavedSearchesDateEnabled) {
        handleDataTablePeriodChange(newPeriod ? { ...newPeriod, isFromStaticPeriod } : null);
        return;
      }

      handleDataTablePeriodChange(newPeriod);
    },
    [handleDataTablePeriodChange, isSavedSearchesDateEnabled]
  );

  useEffect(() => {
    handleEditSearch(
      filters,
      isSavedSearchesDateEnabled ? selectedPeriod : null,
      isSavedSearchesDateEnabled ? dateTypeFilter?.id : null
    );
  }, [dateTypeFilter, filters, handleEditSearch, isSavedSearchesDateEnabled, selectedPeriod]);

  useEffect(() => {
    const shouldRestorePeriod =
      isSavedSearchesDateEnabled &&
      !!appliedPeriod &&
      !!savedSearchRestoreKey &&
      lastAppliedSavedSearchRestoreKey !== savedSearchRestoreKey;

    if (!shouldRestorePeriod) return;

    if (!isEqual(appliedPeriod, selectedPeriod)) handleDataTablePeriodChange(appliedPeriod);

    if (dateTypeFilter?.id !== restoredDateTypeFilter.id) setDateTypeFilter(restoredDateTypeFilter);

    if (activeSection !== restoredDateTypeFilter.id)
      handleSectionChange && handleSectionChange(null, restoredDateTypeFilter.id);

    setLastAppliedSavedSearchRestoreKey(savedSearchRestoreKey);
  }, [
    activeSection,
    appliedPeriod,
    dateTypeFilter.id,
    handleDataTablePeriodChange,
    handleSectionChange,
    isSavedSearchesDateEnabled,
    lastAppliedSavedSearchRestoreKey,
    restoredDateTypeFilter,
    savedSearchRestoreKey,
    selectedPeriod
  ]);

  const { columns, columnOrder, orderBy, direction } = columnPreferences;
  useUrlParamsUpdater({ history, location, orderBy });

  const { isLoading, showWholeSkeleton, isSideMenuOpen, page, perPage, keyword, userFilter } =
    listState;

  const dataForSelection = useMemo(
    () => data.map(({ id }) => ({ id, type_id: 1, type: 'Candidate' })),
    [data]
  );

  const {
    handleRowSelection,
    selectedIndexesInPage,
    totalSelected,
    filteredItems,
    multiSelectComponents
  } = useMultipleSelection({ data: dataForSelection, count, perPage });

  const {
    isDailyPlanTaskDrawerOpen,
    formattedTaskItems,
    allowedTasks,
    tasksQuery,
    handleCloseTaskDrawer,
    handleOpenTaskDrawer,
    handleOnTaskCreated
  } = useTaskDrawerManager({
    table: ORIGIN_TABLE_MAP.Candidates,
    selectedItems: filteredItems.data,
    selectionType: filteredItems.type,
    queryParams
  });

  const { SearchProjectAction, SearchProjectForms } = useSearchProject({
    filteredItems,
    queryParams
  });

  usePageTitle({ title: PageTitles.Candidate, isLoading });

  const isSearchProjectRefactorActive = checkIfFeatureFlagEnabled(
    FeatureFlags.SearchProjectsRefactor
  );

  function handleAction({ rowData, origin }) {
    addRingCentralContactToStore({
      rowData,
      columns: finalColumns,
      dispatch,
      role: EntityType.Candidate,
      origin
    });
  }

  const { entityId, quickViewState, listViewPreset, quickViewEntity, toggleQuickViewPreview } =
    useQuickViewPreview({ columns, data, entityType: EntityType.Candidate });

  const phoneActionButtons = buildRowActionsRendererDefinition({
    actions: [...CommunicationActionPreset, listViewPreset],
    namespace: EntityType.Candidate,
    handleAction,
    columns: CANDIDATE_COLUMNS
  });

  const handleDateTypeFilterChange = (name, value) => {
    setDateTypeFilter(value);
    handleSectionChange && handleSectionChange(name, value.id);
  };

  const finalColumns = getColumnsToRender([...columns, phoneActionButtons]);

  const handleTabRefresh = async () => {
    await getData();
  };

  const shouldSelectRequiredFilters = isBelowMinFiltersThreshold && (!data || data.length === 0);
  const savedSearchButtonDefinitions = getSavedSearchButtonDefinitions({ filters });

  const handleSavedSearchRemove = () => {
    setLastAppliedSavedSearchRestoreKey(null);

    handleRemoveSearch({
      onClearPeriod: isSavedSearchesDateEnabled ? () => handleDataTablePeriodChange(null) : null
    });
  };

  return (
    <FiltersLayout
      title="Candidates"
      section={EntityType.Candidate}
      listSelector={
        <AutocompleteSelect
          name="userFilter"
          placeholder="Candidates to show"
          selectedValue={userFilter || mainOptions[0]}
          onSelect={handleOneFilterChange}
          defaultOptions={mainOptions}
          disableClearable
        />
      }
      defaultRange={appliedPeriod || null}
      extraSelector={
        <AutocompleteSelect
          name="dateTypeFilter"
          placeholder="Filter By"
          selectedValue={dateTypeFilter}
          onSelect={handleDateTypeFilterChange}
          defaultOptions={DateFilters}
          disableClearable
        />
      }
      dateRangerPickerProps={{
        staticRanges: createCustomStaticRanges(additionalPeriodRanges)
      }}
      filters={filters}
      isSideMenuOpen={isSideMenuOpen}
      isLoading={showWholeSkeleton}
      isPeriodClearable
      groups={FiltersGroups}
      enableStore={false}
      onSearch={handleFiltersApply}
      onFiltersChange={handleFiltersChange}
      onMenuToggle={handleFiltersToggle}
      titleLabelProps={backNavigateListConfig}
      onPeriodChange={handlePeriodChange}
      minRequiredFilters={shouldSelectRequiredFilters ? MIN_REQUIRED_FILTERS : null}
      shouldSelectRequiredFilters={shouldSelectRequiredFilters}
      enableSavedSearches={isSavedSearchesEnabled}
      savedSearchesHeader={
        isSavedSearchesEnabled &&
        activeSearch && (
          <SearchApplied
            isDirty={isDirty}
            onRemoveSearch={handleSavedSearchRemove}
            onSaveSearch={!isEmpty(filters) ? handleEditSavedSearchDrawerOpen : null}
            searchName={activeSearch?.name}
          />
        )
      }
      savedSearchesActions={
        isSavedSearchesEnabled && (
          <FPButtonBuilder
            GridContainerProps={{ justify: 'flex-end', spacing: 2 }}
            buttonDefinitions={savedSearchButtonDefinitions}
          />
        )
      }
    >
      {!isSearchProjectRefactorActive && (
        <>
          <DataTable
            isExpandable
            loading={showWholeSkeleton}
            refreshing={isLoading}
            data={data}
            columns={finalColumns}
            count={count}
            filter={false}
            page={page}
            rowsPerPage={perPage}
            searchText={keyword}
            sortOrder={{ name: orderBy, direction }}
            columnOrder={columnOrder?.length && columnOrder}
            draggableColumns={{
              enabled: true
            }}
            enableCustomFilters
            onToggleFilters={handleFiltersToggle}
            onSearchTextChange={handleKeywordChange}
            onColumnSortChange={handleColumnSortChange}
            onPerPageClick={handlePerPageChange}
            onPageClick={handlePageChange}
            onColumnDisplayClick={handleColumnDisplayChange}
            onColumnOrderChange={handleColumnOrderChange}
            onRowSelectionChange={handleRowSelection}
            rowsSelected={selectedIndexesInPage}
            selectToolbarPlacement="none"
            CustomToolbar={
              <CustomTableToolbarSelect total={totalSelected}>
                <When condition={canCreateMultipleTasks}>
                  <CreateMultipleTasksAction onClick={handleOpenTaskDrawer} />
                </When>
                <SearchProjectAction />
              </CustomTableToolbarSelect>
            }
            components={multiSelectComponents}
          />
          <SearchProjectForms />
        </>
      )}
      {isSearchProjectRefactorActive && (
        <TableWithSearchProjects
          isExpandable
          loading={showWholeSkeleton}
          refreshing={isLoading}
          columns={finalColumns}
          filter={false}
          page={page}
          searchText={keyword}
          sortOrder={{ name: orderBy, direction }}
          columnOrder={columnOrder?.length && columnOrder}
          draggableColumns={{
            enabled: true
          }}
          enableCustomFiltersselectToolbarPlacement="none"
          data={data}
          count={count}
          perPage={perPage}
          entity={CandidateEntity.singular}
          queryParams={queryParams}
          onToggleFilters={handleFiltersToggle}
          onSearchTextChange={handleKeywordChange}
          onColumnSortChange={handleColumnSortChange}
          onPerPageClick={handlePerPageChange}
          onPageClick={handlePageChange}
          onColumnDisplayClick={handleColumnDisplayChange}
          onColumnOrderChange={handleColumnOrderChange}
        />
      )}
      <When condition={entityId && quickViewState.isQuickViewOpen}>
        <FPQuickView
          drawerProps={{
            open: quickViewState.isQuickViewOpen
          }}
          entityType={quickViewEntity}
          onClose={toggleQuickViewPreview}
          itemsToNavigate={data}
          id={entityId}
          onEditionCompleted={handleTabRefresh}
        />
      </When>
      <When
        condition={isDailyPlanTaskDrawerOpen && (formattedTaskItems.length > 0 || !!tasksQuery)}
      >
        <CreationTaskDrawer
          onClose={handleCloseTaskDrawer}
          listItems={formattedTaskItems}
          allowedTaskTypes={allowedTasks}
          tasksQuery={tasksQuery}
          totalSelected={totalSelected}
          onTaskCreated={handleOnTaskCreated}
        />
      </When>

      <When condition={isSavedSearchesEnabled && savedSearchesUiState.isSavedSearchDrawerOpen}>
        <SavedSearchDrawer
          activeSearch={activeSearch}
          entityType={EntityType.Candidate}
          onApplySearch={handleApplySearch}
          onClose={handleCloseSavedSearchDrawer}
          onDeleteSearch={handleDeleteSearch}
          open={savedSearchesUiState.isSavedSearchDrawerOpen}
        />
      </When>

      <When
        condition={
          isSavedSearchesEnabled &&
          (savedSearchesUiState.isCreationSavedSearchDrawerOpen ||
            savedSearchesUiState.isEditSavedSearchDrawerOpen)
        }
      >
        <CreationSavedSearchDrawer
          activeSearch={activeSearch}
          entityType={EntityType.Candidate}
          onApplySearch={handleApplySearch}
          onSaveCompleted={handleCloseSavedSearchForm}
          onClose={handleCloseSavedSearchForm}
          open
          savedSearchId={savedSearchesUiState.isEditSavedSearchDrawerOpen ? activeSearch?.id : null}
          search={currentSavedSearchPayload}
        />
      </When>
    </FiltersLayout>
  );
};

const mapDispatchToProps = dispatch => {
  return {
    onShowAlert: alert => dispatch(showAlert(alert))
  };
};

export default connect(null, mapDispatchToProps)(Candidates);
